import { css, styled } from 'next-yak';

import { MiniBadge as MiniMadgeComponent } from '@xing-com/badge';
import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import {
  motionEasingEnter,
  motionTimeS,
  motionTimeXXS,
  scale010,
  scale130,
  scale150,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorButtonSecondaryHover,
  xdlColorControlFillTertiary,
  xdlColorFocusRing,
  xdlColorHoverTertiary,
  xdlColorProJobs,
  xdlColorProJobsFill,
  xdlColorSelected,
  xdlColorText,
  xdlColorTextInvert,
  xdlColorTextOnLight,
} from '@xing-com/tokens';

import {
  BUTTON_BORDER_SIZE,
  BUTTON_MARGIN_RIGHT,
  BUTTON_PADDING,
  FONT_SIZES,
  ICON_MARGIN_RIGHT,
  MINI_BADGE_MARGIN_RIGHT,
} from '../constants';
import type {
  ContentSwitcherSize,
  ContentSwitcherVariant,
} from '../content-switcher/content-switcher.types';

type ButtonTransientProps = {
  $selected: boolean;
  $size?: ContentSwitcherSize;
  $sizeConfined?: ContentSwitcherSize;
  $sizeWide?: ContentSwitcherSize;
  $variant?: ContentSwitcherVariant;
};

// *** Base ***
const baseButton = css`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 50px;
  box-sizing: border-box;
  color: ${xdlColorText};
  cursor: pointer;
  display: flex;
  font-weight: 700;
  justify-content: center;
  margin-right: ${BUTTON_MARGIN_RIGHT};
  padding-left: ${BUTTON_PADDING};
  padding-right: ${BUTTON_PADDING};
  position: relative;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;

  &::before {
    background-color: transparent;
    border-color: ${xdlColorA11yBorder};
    border-style: solid;
    border-width: ${BUTTON_BORDER_SIZE};
    border-radius: 50px;
    content: '';
    inset: 0;
    position: absolute;
    transition-duration: ${motionTimeS}, ${motionTimeXXS};
    transition-property: background-color, transform;
    transition-timing-function: ${motionEasingEnter};
  }
  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  @media (hover: hover) {
    &:hover::before {
      background-color: ${xdlColorButtonSecondaryHover};
      outline: 0; /* when there is a focus or hover the button is changing the background color so we can remove the outline that looks really bad */
      text-decoration: none;
      transform: scale(1.05);
    }
  }

  &:active {
    color: ${xdlColorTextInvert};

    &::before {
      background-color: ${xdlColorSelected};
      transform: scale(0.95);
    }
  }
`;

// *** Sizes ***
export const sizes = {
  small: css`
    font-size: ${FONT_SIZES.small};
    height: ${scale130};
  `,
  medium: css`
    font-size: ${FONT_SIZES.medium};
    height: ${scale150};
  `,
};

const selectedStyle = css`
  &&& {
    color: ${xdlColorTextInvert};

    &::before {
      background-color: ${xdlColorSelected};
      border-color: ${xdlColorSelected};
    }
  }
`;

const filledStyle = css`
  && {
    &::before {
      background-color: ${xdlColorControlFillTertiary};
    }

    @media (hover: hover) {
      &:hover::before {
        background-color: ${xdlColorHoverTertiary};
        transform: scale(1.05);
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::before {
        background-color: ${xdlColorSelected};
        transform: scale(0.95);
      }
    }
  }
`;

const projobsStyle = css`
  && {
    color: ${xdlColorTextOnLight};
    text-decoration: none;

    &::before {
      background-color: ${xdlColorProJobsFill};
    }

    @media (hover: hover) {
      &:hover::before {
        background-color: ${xdlColorProJobs};
        transform: scale(1.05);
      }
    }

    &:active {
      color: ${xdlColorTextOnLight};

      &::before {
        background-color: ${xdlColorSelected};
        transform: scale(0.95);
      }
    }
  }
`;

// *** Components ***
const ButtonElement = createPolymorphicElement('button', ['copy']);

const Button = styled(ButtonElement)<ButtonTransientProps>`
  ${baseButton};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $variant }) => $variant === 'filled' && filledStyle};
  ${({ $variant }) => $variant === 'proJobs' && projobsStyle};
  ${({ $selected }) => $selected && selectedStyle};
`;

const RouterLink = styled(Link)<ButtonTransientProps>`
  ${baseButton};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $variant }) => $variant === 'filled' && filledStyle};
  ${({ $variant }) => $variant === 'proJobs' && projobsStyle};
  ${({ $selected }) => $selected && selectedStyle};
`;

const MiniBadge = styled(MiniMadgeComponent)`
  margin-right: ${MINI_BADGE_MARGIN_RIGHT};
`;

const Inner = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
  white-space: nowrap;
`;

const ButtonIcon = styled.div`
  line-height: 0;
  margin-right: ${ICON_MARGIN_RIGHT};
`;

export const Styled = {
  Button,
  ButtonIcon,
  Inner,
  MiniBadge,
  RouterLink,
};
